// ═══════════════════════════════════════════════════════════════
// SHELTER — Supply Distribution
// Quick handout interface: scan/select item, set quantity, mark distributed
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Stack, SimpleGrid, NumberInput, Center, Loader, Box, Divider,
} from '@mantine/core';
import {
  IconSearch, IconMinus, IconPackage, IconCheck, IconHistory,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { SUPPLY_CATEGORIES } from '../../types/shelter';
import type { ShelterSupply, SupplyCategory } from '../../types/shelter';

const CATEGORY_COLORS: Record<SupplyCategory, string> = {
  bedding: 'blue', food: 'green', water: 'cyan', hygiene: 'grape',
  medical: 'red', equipment: 'orange', other: 'gray',
};

interface DistLog {
  id: string;
  itemName: string;
  qty: number;
  time: string;
  recipientName: string;
}

export default function SupplyDistribution() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    supplies, shelters, loadingSupplies,
    loadSupplies, loadShelters, updateSupplyItem,
  } = useShelterStore();

  const [search, setSearch] = useState('');
  const [shelterFilter, setShelterFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [distQty, setDistQty] = useState<Record<number, number>>({});
  const [recipientName, setRecipientName] = useState('');
  const [distributing, setDistributing] = useState<number | null>(null);
  const [distLog, setDistLog] = useState<DistLog[]>([]);

  useEffect(() => { loadSupplies(); loadShelters(); }, [loadSupplies, loadShelters]);

  const incSupplies = useMemo(
    () => supplies.filter(s => !incidentId || String(s.incidentId) === incidentId),
    [supplies, incidentId],
  );

  const filtered = useMemo(() => {
    return incSupplies
      .filter(s => s.quantity > 0)
      .filter(s => !shelterFilter || String(s.shelterId) === shelterFilter)
      .filter(s => {
        if (!categoryFilter) return true;
        // Match exact category OR partial (e.g. "food" matches "food", "food_water")
        return s.category === categoryFilter || s.category.includes(categoryFilter);
      })
      .filter(s => {
        if (!search) return true;
        const q = search.toLowerCase();
        return s.itemName.toLowerCase().includes(q) || s.category.toLowerCase().includes(q);
      })
      .sort((a, b) => a.itemName.localeCompare(b.itemName));
  }, [incSupplies, shelterFilter, categoryFilter, search]);

  const shelterOptions = useMemo(
    () => shelters.map(s => ({ value: String(s.id), label: s.name })),
    [shelters],
  );

  const handleDistribute = async (item: ShelterSupply) => {
    const qty = distQty[item.id] || 1;
    if (qty <= 0 || qty > item.quantity) {
      notifications.show({ message: 'Invalid quantity', color: 'red' });
      return;
    }
    setDistributing(item.id);
    try {
      await updateSupplyItem(item.id, { quantity: item.quantity - qty });
      setDistLog(prev => [{
        id: `${Date.now()}`,
        itemName: item.itemName,
        qty,
        time: new Date().toLocaleTimeString(),
        recipientName: recipientName || 'Walk-up',
      }, ...prev]);
      setDistQty(prev => ({ ...prev, [item.id]: 1 }));
      notifications.show({
        message: `Distributed ${qty}x ${item.itemName}`,
        color: 'green',
        icon: <IconCheck size={14} />,
      });
    } catch {
      notifications.show({ message: 'Failed to update supply', color: 'red' });
    } finally {
      setDistributing(null);
    }
  };

  if (loadingSupplies && supplies.length === 0) {
    return <Center py="xl"><Loader /></Center>;
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={3} c="black">Supply Distribution</Title>
          <Text size="sm" c="dimmed">Quickly hand out supplies and track distribution.</Text>
        </Box>
        <Badge size="lg" variant="light">{filtered.length} items available</Badge>
      </Group>

      {/* Recipient + Filters */}
      <Paper p="md" withBorder radius="sm" mb="md">
        <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="sm">
          <TextInput
            label="Recipient Name"
            placeholder="Optional — person receiving"
            value={recipientName}
            onChange={e => setRecipientName(e.currentTarget.value)}
            size="xs"
          />
          <TextInput
            label="Search Items"
            placeholder="Search..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            size="xs"
          />
          <Select
            label="Shelter"
            placeholder="All shelters"
            data={shelterOptions}
            value={shelterFilter}
            onChange={setShelterFilter}
            clearable
            size="xs"
          />
          <Select
            label="Category"
            placeholder="All categories"
            data={SUPPLY_CATEGORIES.map(c => ({ value: c.value, label: c.label }))}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            size="xs"
          />
        </SimpleGrid>
      </Paper>

      {/* Supply Grid */}
      {filtered.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconPackage size={40} color="#d1d5db" style={{ opacity: 0.3 }} />
            <Text c="dimmed">No items with available stock match your filters.</Text>
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          {filtered.map(item => {
            const isLow = item.reorderThreshold > 0 && item.quantity <= item.reorderThreshold;
            const qty = distQty[item.id] ?? 1;
            return (
              <Paper key={item.id} p="md" withBorder radius="sm"
                style={{ borderLeft: `3px solid var(--mantine-color-${CATEGORY_COLORS[item.category as SupplyCategory] || 'gray'}-5)` }}>
                <Group justify="space-between" mb="xs">
                  <Text fw={600} size="sm">{item.itemName}</Text>
                  <Badge size="xs" color={CATEGORY_COLORS[item.category as SupplyCategory] || 'gray'}>
                    {SUPPLY_CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                  </Badge>
                </Group>
                <Group gap="xs" mb="sm">
                  <Text size="xl" fw={700} c={isLow ? 'red' : undefined}>{item.quantity}</Text>
                  <Text size="xs" c="dimmed">{item.unit} in stock</Text>
                  {isLow && <Badge size="xs" color="red" variant="light">Low Stock</Badge>}
                </Group>
                <Group gap="xs">
                  <NumberInput
                    value={qty}
                    onChange={v => setDistQty(prev => ({ ...prev, [item.id]: typeof v === 'number' ? v : 1 }))}
                    min={1}
                    max={item.quantity}
                    size="xs"
                    style={{ width: 80 }}
                  />
                  <Button
                    size="xs"
                    color="teal"
                    leftSection={<IconMinus size={12} />}
                    onClick={() => handleDistribute(item)}
                    loading={distributing === item.id}
                    disabled={qty <= 0 || qty > item.quantity}
                  >
                    Hand Out
                  </Button>
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {/* Distribution Log */}
      {distLog.length > 0 && (
        <Paper p="md" withBorder radius="sm" mt="lg">
          <Group gap="xs" mb="sm">
            <IconHistory size={16} />
            <Text fw={600} size="sm">Session Distribution Log ({distLog.length})</Text>
          </Group>
          <Divider mb="sm" />
          <Stack gap={4}>
            {distLog.map(entry => (
              <Group key={entry.id} justify="space-between" px="xs" py={2}
                style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }}>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">{entry.time}</Text>
                  <Text size="sm" fw={500}>{entry.qty}x {entry.itemName}</Text>
                </Group>
                <Text size="xs" c="dimmed">{entry.recipientName}</Text>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}
    </div>
  );
}
